import React, { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchNotifications } from '../../hooks/useFetchNotifications';
import { groupNotificationsByDay } from '../../helpers/notification';
import { useLanguage } from '../../context/LanguageContext';
import CustomTabs from '../customTabs/customTabs';
import { useNotificationActions } from '../../hooks/useNotificationActions';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import NotificationTabContent from './NotificationTabContent';
import { IGroupedNotifications, IInAppNotification } from '../../interface';
import { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';

interface NotificationListProps {
  isOpen: boolean;
  onClose: () => void;
}

const useNotificationData = (activeTab: string, searchValue: string) => {
  const {
    data: notificationList,
    isFetching,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    error,
  } = useFetchNotifications({
    unreadOnly: activeTab === 'unread' ? 'true' : 'false',
    ...(activeTab === 'unread' ? {} : { search: searchValue }),
  });

  const groupedNotifications = groupNotificationsByDay(notificationList ?? []);

  return {
    notificationList,
    groupedNotifications,
    isFetching,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    error,
  };
};

const NotificationTabs: React.FC<{
  activeTab: string;
  setActiveTab: (tab: string) => void;
  groupedNotifications: IGroupedNotifications[];
  notificationList: IInAppNotification[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  language: string;
  onMarkAllAsRead: () => void;
  onNotificationClick: (notification: IInAppNotification) => void;
  onRefresh: () => void | Promise<unknown>;
  onSearch: (value: string) => void;
  loadMoreRef?: (node?: Element | null) => void;
  rootReference: RefObject<Element | null>;
  t: TFunction;
  error: Error | null;
  onRetry: () => void;
}> = ({
  activeTab,
  setActiveTab,
  groupedNotifications,
  notificationList,
  isLoading,
  isFetching,
  hasNextPage,
  language,
  onMarkAllAsRead,
  onNotificationClick,
  onRefresh,
  onSearch,
  loadMoreRef,
  rootReference,
  t,
  error,
  onRetry,
}) => {
  const items = [
    {
      key: 'unread',
      label: t('common:tab.unreadNotifications'),
      children: (
        <NotificationTabContent
          tab="unread"
          activeTab={activeTab}
          groupedNotifications={groupedNotifications}
          notificationList={notificationList}
          isLoading={isLoading}
          isFetching={isFetching}
          hasNextPage={hasNextPage}
          language={language}
          onMarkAllAsRead={onMarkAllAsRead}
          onRefresh={onRefresh}
          onNotificationClick={onNotificationClick}
          loadMoreRef={loadMoreRef}
          rootRef={(el) => {
            rootReference.current = el as Element;
          }}
          markAllAsReadText={t('common:markAllAsRead')}
          noNotificationsText={t('common:noUnreadNotifications')}
          noNotificationsSubText={t('common:noUnreadNotificationsSubText')}
          error={error}
          onRetry={onRetry}
        />
      ),
    },
    {
      key: 'all',
      label: t('common:tab.allNotifications'),
      children: (
        <NotificationTabContent
          tab="all"
          activeTab={activeTab}
          groupedNotifications={groupedNotifications}
          notificationList={notificationList}
          isLoading={isLoading}
          isFetching={isFetching}
          hasNextPage={hasNextPage}
          language={language}
          onMarkAllAsRead={onMarkAllAsRead}
          onSearch={onSearch}
          onRefresh={onRefresh}
          onNotificationClick={onNotificationClick}
          loadMoreRef={loadMoreRef}
          rootRef={(el) => {
            rootReference.current = el as Element;
          }}
          markAllAsReadText={t('common:markAllAsRead')}
          searchPlaceholder={t('common:searchNotifications')}
          noNotificationsText={t('common:noAllNotifications')}
          noNotificationsSubText={t('common:noAllNotificationsSubText')}
          error={error}
          onRetry={onRetry}
        />
      ),
    },
  ];

  return (
    <CustomTabs
      items={items}
      destroyOnHidden
      onChange={(key) => {
        setActiveTab(key);
      }}
      data-testid="notification-tabs"
    />
  );
};

const NotificationList: React.FC<NotificationListProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>('unread');
  const [searchValue, setSearchValue] = useState<string>('');
  const queryClient = useQueryClient();
  const {
    notificationList,
    groupedNotifications,
    isFetching,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    error,
  } = useNotificationData(activeTab, searchValue);

  const { handleNotificationClick, handleMarkAllAsRead } = useNotificationActions(onClose, refetch);

  const { rootReference, loadMoreRef } = useNotificationSync({
    isOpen,
    activeTab,
    hasNextPage,
    isFetchingNextPage,
    notificationListLength: notificationList?.length ?? 0,
    refetch,
    fetchNextPage,
  });

  return (
    <NotificationTabs
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      groupedNotifications={groupedNotifications}
      notificationList={notificationList}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      language={language}
      onMarkAllAsRead={handleMarkAllAsRead}
      onNotificationClick={handleNotificationClick}
      onRefresh={() => {
        queryClient.removeQueries({
          queryKey: ['notification-list'],
        });
        return refetch();
      }}
      onSearch={setSearchValue}
      loadMoreRef={loadMoreRef}
      rootReference={rootReference}
      t={t}
      error={error}
      onRetry={() => {
        queryClient.removeQueries({
          queryKey: ['notification-list'],
        });
        refetch();
      }}
    />
  );
};

export default NotificationList;
